import { useEffect, useState } from 'react'

import type {
  CameraVideoTrackInitConfig,
  ILocalVideoTrack,
} from 'agora-rtc-sdk-ng'

import { createCameraVideoTrack } from '@core/agoraRTC/libs/utils'
import { cameraIdSelector, facingModeSelector } from '@core/deviceSettings'
import { useGlobalStore } from '@core/hooks/useGlobalStore'

export interface UseCameraParams {
  allowed: boolean
  config?: Omit<CameraVideoTrackInitConfig, 'cameraId' | 'facingMode'>
}

/**
 * Hook that creates and manages the camera video track.
 * It creates a new track on every change of the camera id.
 *
 * @param allowed Whether to create the track or not.
 * @params config - When passing a config, declare the config outside the component / hook scope to avoid creating an infinite render loop.

 */
export function useCamera({
  allowed,
  config,
}: UseCameraParams): ILocalVideoTrack | undefined {
  const [videoTrack, setVideoTrack] = useState<ILocalVideoTrack>()
  const cameraId = useGlobalStore(cameraIdSelector)
  const facingMode = useGlobalStore(facingModeSelector)

  useEffect(() => {
    if (allowed) {
      createCameraVideoTrack({
        ...config,
        cameraId,
        facingMode,
      }).then(setVideoTrack)
    } else {
      setVideoTrack(undefined)
    }
  }, [allowed, cameraId, facingMode, config])

  useEffect(() => {
    return () => {
      if (videoTrack) {
        videoTrack.close()
      }
    }
  }, [videoTrack])

  return videoTrack
}
